import type { InStationDetectionSettings } from '~/composables/common/useSystemConfig';
import type {
  GeolocationAcquisitionError,
  PositionValidationArea,
} from '~/stores/geolocations/geolocationStore';

/**
 * 駅エリア判定に必要な入力情報。
 *
 * composable/storeに依存せず、判定に必要な値だけを受け取る。
 */
export type AreaJudgementInput = {
  /** 直近取得した現在位置。初回取得待ちや取得失敗時はnull */
  position: GeolocationPosition | null;
  /** 位置情報取得時に発生したエラー情報。初回取得待ちや正常時はnull */
  acquisitionError: GeolocationAcquisitionError | null;
  /** 駅エリア判定に使用する矩形範囲。駅情報未取得時はnull */
  area: PositionValidationArea | null;
  /** 現在までに連続してエリア内判定となった回数 */
  consecutiveSuccess: number;
  /** 駅エリア検知のシステム設定 */
  settings: InStationDetectionSettings;
};

/**
 * 駅エリア判定の結果。
 *
 * composable側はこの結果を元にログ出力とstore更新を行う。
 */
export type AreaJudgementResult = {
  /** 判定結果の分類 */
  status: 'waiting' | 'inside' | 'outside' | 'error' | 'skipped';
  /** geolocationStoreのエリア判定関連フィールドへ反映する値 */
  areaFields?: {
    /** 現在位置が駅エリア内と判定されているか */
    isInsideArea?: boolean;
    /** エリア内判定が連続成功した回数 */
    consecutiveSuccess?: number;
    /** 駅エリア判定時に表示するエラーメッセージ */
    validationError?: string | null;
  };
  /** composable側でログ出力するための補足情報 */
  log?: {
    /** 判定に使用した緯度 */
    latitude: number;
    /** 判定に使用した経度 */
    longitude: number;
    /** 判定に使用した位置情報の精度（メートル） */
    accuracy: number;
    /** 矩形範囲内かどうか */
    isInside?: boolean;
    /** 判定後の連続成功回数 */
    consecutiveSuccess?: number;
    /** エリア内確定に必要な連続成功回数 */
    stayThreshold?: number;
    /** 判定に使用した駅エリア範囲 */
    area?: PositionValidationArea;
  };
};

/**
 * 駅エリア内外を判定し、storeへ反映する値を返す。
 *
 * @param input 駅エリア判定に必要な位置情報、判定範囲、連続成功回数、設定値
 * @returns 判定結果、store更新値、ログ出力用情報
 */
export const judgeAreaPosition = ({
  position,
  acquisitionError,
  area,
  consecutiveSuccess,
  settings,
}: AreaJudgementInput): AreaJudgementResult => {
  // 判定範囲がない場合は、エリア検知を継続できないためエラー扱いにする。
  if (!area) {
    return {
      status: 'error',
      areaFields: { validationError: 'エリア情報が設定されていません' },
    };
  }

  // 駅エリア検知フラグがfalseの場合は、判定自体をスキップして常にエリア内とみなす。
  if (area.isAreaDetectionEnabled === false) {
    return {
      status: 'skipped',
      areaFields: { isInsideArea: true, validationError: null },
    };
  }

  // 緯度経度が未設定（0含む）の場合は、判定できる範囲がないためOK扱いにする。
  if (!area.lat1 || !area.lat2 || !area.lon1 || !area.lon2) {
    return {
      status: 'skipped',
      areaFields: { isInsideArea: true, validationError: null },
    };
  }

  // 位置情報取得エラー中はエラー優先。
  // 過去のpositionが残っていても、古い位置でエリア内と判定しないようにする。
  if (acquisitionError !== null) {
    return {
      status: 'error',
      areaFields: {
        validationError: '位置情報の取得に失敗しました',
        isInsideArea: false,
        consecutiveSuccess: 0,
      },
    };
  }

  // 位置未取得（watchPositionの初回取得待ち）はエラー扱いにせず、次回判定を待つ。
  if (!position) {
    return { status: 'waiting' };
  }

  // 以降の判定・ログ出力で共通して使用する位置情報を取り出す。
  const accuracy = position.coords.accuracy;
  const latitude = position.coords.latitude;
  const longitude = position.coords.longitude;
  const log = {
    latitude,
    longitude,
    accuracy,
  };

  // 精度が設定値を超える場合は、位置の信頼性が足りないためエリア外扱いにする。
  if (accuracy === undefined || accuracy > settings.maxAccuracy) {
    const accuracyStr = accuracy !== undefined ? `${accuracy}m` : '不明';
    return {
      status: 'error',
      areaFields: {
        validationError: `位置情報の精度が不十分です (${accuracyStr} > ${settings.maxAccuracy}m)`,
        isInsideArea: false,
        consecutiveSuccess: 0,
      },
      log,
    };
  }

  // 現在位置が駅エリアの矩形範囲内に収まっているかを判定する。
  const isInside
    = latitude >= area.lat1
      && latitude <= area.lat2
      && longitude >= area.lon1
      && longitude <= area.lon2;

  // エリア内の場合は連続成功回数を加算し、閾値到達後にエリア内確定とする。
  if (isInside) {
    const newConsecutive = consecutiveSuccess + 1;
    return {
      status: 'inside',
      areaFields: {
        consecutiveSuccess: newConsecutive,
        isInsideArea: newConsecutive >= settings.stayThreshold,
        validationError: null,
      },
      log: {
        ...log,
        isInside,
        consecutiveSuccess: newConsecutive,
        stayThreshold: settings.stayThreshold,
      },
    };
  }

  // エリア外の場合は連続成功回数をリセットし、画面表示用のエラーを設定する。
  return {
    status: 'outside',
    areaFields: {
      consecutiveSuccess: 0,
      isInsideArea: false,
      validationError: 'エリア外にいます',
    },
    log: {
      ...log,
      isInside,
      area,
    },
  };
};
